import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { InternalUserAuthViewModel } from '../internal-user-auth-view-model.service';
import { SessionService } from '../../../../../../services/session-service';
import { ChoosePasswordRequest } from '../../../../../../models/users/requests/choose-password-request';
import { passwordComplexityValidator } from '../../../../../../validators/password-complexity.validator';
import { fieldMatchValidator } from '../../../../../../validators/field-match.validator';
import { ButtonTypeEnum } from '../../../../../../ui/buttons/button/button-type.enum';
import { PasswordInputComponent } from '../../../../../../ui/form-components/password-input/password-input.component';

@Component({
  selector: 'app-internal-user-choose-password',
  templateUrl: './internal-user-choose-password.component.html',
  styleUrls: ['./internal-user-choose-password.component.scss', '../../auth.component.scss']
})
export class InternalUserChoosePasswordComponent implements AfterViewInit {
  private viewModel = inject(InternalUserAuthViewModel);
  private sessionService = inject(SessionService);

  @ViewChild('passwordInput', { static: true }) passwordInput!: PasswordInputComponent;

  public authFlowTitle$ = this.viewModel.authFlowTitle$;
  public authFlowSubtext$ = this.viewModel.authFlowSubtext$;

  public formSubmitted = false;

  public formGroup = new FormGroup(
    {
      email: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, passwordComplexityValidator]
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      eulaAccepted: new FormControl<boolean>(false, {
        nonNullable: true,
        validators: [Validators.requiredTrue]
      })
    },
    { validators: fieldMatchValidator('password', 'confirmPassword') }
  );

  public customErrorMessages = {
    password: {
      required: $localize`Password is required`,
      passwordComplexity: $localize`Password does not meet requirements`
    },
    confirmPassword: {
      required: $localize`Confirm password is required`,
      fieldsMustMatch: $localize`Passwords must match`
    },
    eulaAccepted: {
      required: $localize`Terms and Conditions must be accepted in order to continue`
    }
  };

  private challengeName = '';
  private sessionId = '';

  constructor() {
    this.sessionService.sessionContainer$.pipe(take(1)).subscribe(session => {
      const container = session ?? this.viewModel.getCachedSession();
      const user = container?.user;
      this.formGroup.controls.email.setValue(user?.email ?? '');
      this.challengeName = user?.challengeName ?? '';
      this.sessionId = user?.sessionId ?? '';
    });
  }

  public ngAfterViewInit(): void {
    this.passwordInput.input.nativeElement.focus();
  }

  public setNewPassword(): void {
    this.formSubmitted = true;
    if (!this.formGroup.valid) return;
    const { email, password, confirmPassword, eulaAccepted } = this.formGroup.getRawValue();
    const req = new ChoosePasswordRequest(
      email,
      password,
      confirmPassword,
      this.challengeName,
      this.sessionId,
      eulaAccepted
    );
    this.viewModel.setNewInternalUserPassword(req);
  }

  public openEulaModal(): void {
    this.formGroup.controls.eulaAccepted.setValue(false);
    this.viewModel.openEulaModal();
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
